import React, { useState, useImperativeHandle } from "react";
import "./GuardiaTable.css";
// Trae la clase `.gv-plate` que pinta la patente con el borde de la vista "por vehículo".
import "./GuardiaVehicleTable.css";
import { Box, Typography, CircularProgress, IconButton, Tooltip } from "@material-ui/core";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import { TableWrapper, Table, TableHeader, TableBody, TableRow } from "./Table";
import GuardiaCardMobile from "./GuardiaCardMobile";
import { FechaCell, PersonaCell, HostCell, ExitButton } from "./GuardiaCells";
import { PatenteChip } from "./VehicleUnitCells";
import PersonExitDialog from "../Dialog/PersonExitDialog";
import { useLogisticParkNavigation } from "../../../../views/LogisticPark/RegistroIngreso/hooks/useLogisticParkNavigation";
import {
  buildPersonExitPrefill,
  ExitUnitSelection,
} from "../../../../views/LogisticPark/RegistroIngreso/utils/exitPrefillUtils";
import { TransportAuthorization } from "../../../../passapp-sdk/types/transportTypes";
import { TransportEmployeeNumber } from "../../../../store/types/userTypes";
import type { AssociatedVehicleInside } from "../../../../passapp-sdk/types/userTypes";
import { TransportLocation } from "../../../../utils/constants/locationConstants";
import { getEntryDock, getEntryTransportAuthorization } from "./utils/entryAuthorization";
import { getPersonPlates } from "./utils/personPlates";
import EditAssignedDockDialog from "../Dialog/EditAssignedDockDialog";
import type { EditDockTarget } from "../Dialog/EditAssignedDockDialog";

export const TIPOS_GUARDIA_CARD = {
  Fijo: { variant: "blue", label: "Fijo" },
  Contratista: { variant: "purple", label: "Contratista" },
  Eventual: { variant: "orange", label: "Eventual" },
};

type TipoGuardia = "Fijo" | "Contratista" | "Eventual";

type AuthorizationTag =
  | "employee"
  | "visit"
  | "contractor"
  | "eventual"
  | "preauthorized"
  | "guard"
  | "transport";

/** Vehículo tal como lo devuelve el evento: además de la patente trae marca, seguro, etc. */
export interface EventVehicle {
  id?: string;
  plate?: string;
  [key: string]: any;
}

export interface GuardiaRow {
  fecha: string; // ISO datetime string e.g. "2026-03-20T10:06:00"
  nombre: string;
  dni: string;
  // tipo: TipoGuardia;
  lote: string;
  empresa?: string;
  patente?: string;
  turno?: string;
  puesto?: string;
  estado?: string;
  ingreso?: string | null;
  egreso?: string | null;
  eventId?: string;
  userId?: string;
  mainEvent?: string;
  countryCode?: string;
  facePhoto?: string;
  frontDNI?: string;
  backDNI?: string;
  inEventComment?: string;
  inEventPeopleComment?: string;
  inEventPhoto?: string;
  inEventPhoto2?: string;
  inEventPhoto3?: string;
  vehicle?: any;
  /** Unidades con las que entró —la principal incluida—, cada una con su estado. Autoritativo: lo
   * resuelve el backend contra el último evento de cada vehículo, igual que en la vista por
   * vehículo. Vacío en ingresos viejos, donde sólo quedan las patentes de `event.trailers`. */
  associatedVehicles?: AssociatedVehicleInside[];
  vehicleType?: string;
  email?: string;
  phone?: string;
  /** Legajo del chofer, para prefillear el formulario de transporte al marcar el egreso. */
  transportEmployeeNumber?: string;
  transportEmployeeNumbers?: TransportEmployeeNumber[];
  /** Empresa de transporte con la que entró. Vacía fuera del flujo de transportistas. */
  transportCompanyName?: string;
  birthdate?: string;
  sex?: string;
  host?: {
    firstname?: string;
    lastname?: string;
    company?: string;
  };
  event?: {
    guestType?: string;
    authorizationTags?: AuthorizationTag[];
    transportAuthorizations?: TransportAuthorization[];
    /** Invitaciones / confirmaciones en guardia con las que entró. `authorizedBy` es el responsable
     * de texto libre, cuando la autorización la aprobó el guardia y no hay anfitrión. */
    invites?: any[];
    /** N° de remito cargado por el guardia. La autorización sólo lo trae en egreso. */
    receipt?: string | null;
    /** N° de precinto cargado por el guardia al ingresar. */
    seal?: string | null;
    /** Pallets / bultos declarados al ingresar. */
    pallets?: number | string | null;
    /** Fotos del evento indexadas por fileType (`observationsPhoto`, `observationsPhoto2`, …). */
    photos?: Record<string, string | undefined>;
    /** Vehículo principal del ingreso, con marca, modelo y color. */
    vehicle?: EventVehicle;
    /** Acoplados con los que entró: vehículos completos, o ids sueltos en eventos viejos. */
    trailers?: (EventVehicle | string)[];
    /** Procedencia declarada al ingresar. */
    transportOrigin?: TransportLocation | null;
    /** Destino declarado al egresar. */
    transportDestination?: TransportLocation | null;
  };
}

// Se reexportan desde acá porque media app los importa de este módulo; la implementación vive en
// utils/ para que los agrupadores de filas no dependan del componente.
export { getEntryTransportAuthorization, getEntryDock } from "./utils/entryAuthorization";

type GuardiaTableProps = {
  rows: GuardiaRow[];
  isLoading?: boolean;
  /** Recarga el listado luego de que el backend confirma el cambio de dock. */
  onDockUpdated?: () => void;
};

/** Método imperativo para abrir el modal de salida de una fila desde afuera de la tabla — hoy lo
 * usa el atajo de Enter en el buscador de Egresos, que necesita disparar la misma acción que el
 * click en `ExitButton` sin duplicar el estado de `exitRow` en cada pantalla. */
export type GuardiaTableHandle = {
  triggerExit: (row: GuardiaRow) => void;
};

/**
 * Personas dentro del complejo.
 *
 * La salida no se registra acá: el modal pregunta si la persona sale a pie o con sus unidades, y
 * con eso se abre el formulario de egreso, que es el que hace el `addEvent`. Vive en la tabla —y no
 * en cada pantalla— para que Home y Egresos se comporten igual, como ya hace `GuardiaVehicleTable`.
 */
const GuardiaTable = React.forwardRef<GuardiaTableHandle, GuardiaTableProps>(
  ({ rows, isLoading, onDockUpdated }, ref) => {
    const sortedRows = rows;
    const { navigateToRegistration } = useLogisticParkNavigation();
    const [exitRow, setExitRow] = useState<GuardiaRow | null>(null);
    const [dockTarget, setDockTarget] = useState<EditDockTarget | null>(null);

    useImperativeHandle(ref, () => ({ triggerExit: setExitRow }), []);

    const handleExitConfirm = (selected: ExitUnitSelection[]) => {
      const row = exitRow;
      setExitRow(null);
      if (row) navigateToRegistration("exit", buildPersonExitPrefill(row, selected));
    };

    const openDockEditor = (row: GuardiaRow) => {
      setDockTarget({
        eventId: row.eventId,
        nombre: row.nombre,
        transportCompanyName: row.transportCompanyName,
        patente: row.patente,
        fecha: row.fecha,
        dock: getEntryDock(row),
      });
    };

    return (
      <>
        {/* // MOBILE */}
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          {isLoading ? (
            <Box sx={{ p: 4, textAlign: "center", bgcolor: "#fff", borderRadius: "12px", mt: 2 }}>
              <CircularProgress size={40} />
            </Box>
          ) : !sortedRows || sortedRows.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center", bgcolor: "#fff", borderRadius: "12px", mt: 2 }}>
              <Typography variant="h6" color="textSecondary">
                No hay resultados
              </Typography>
              <Typography variant="body2" color="textSecondary">
                No se encontraron registros para mostrar.
              </Typography>
            </Box>
          ) : (
            sortedRows.map((r, i) => (
              <GuardiaCardMobile
                key={i}
                data={r}
                onMarkExit={setExitRow}
                onEditDock={openDockEditor}
                style={
                  i === 0 || i === sortedRows.length - 1
                    ? {
                        borderTopLeftRadius: i === 0 ? 20 : 0,
                        borderTopRightRadius: i === 0 ? 20 : 0,
                        borderBottomLeftRadius: i === sortedRows.length - 1 ? 20 : 0,
                        borderBottomRightRadius: i === sortedRows.length - 1 ? 20 : 0,
                      }
                    : {}
                }
              />
            ))
          )}
        </Box>
        {/* // DESKTOP */}
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <TableWrapper>
            <Table>
              <TableHeader>
                <TableRow>
                  <th>FECHA ENTRADA</th>
                  <th>PERSONA</th>
                  <th>PATENTE</th>
                  <th>EMPRESA DE TRANSPORTE</th>
                  <th>ANFITRIÓN</th>
                  <th>DOCK ASIGNADO</th>
                  <th>ACCIÓN</th>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <td colSpan={7}>
                      <Box
                        sx={{
                          p: 8,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress size={50} />
                      </Box>
                    </td>
                  </TableRow>
                ) : !rows || rows.length === 0 ? (
                  <TableRow>
                    <td colSpan={7}>
                      <Box sx={{ p: 4, textAlign: "center" }}>
                        <Typography variant="h6" color="textSecondary">
                          No hay resultados
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          No se encontraron registros para mostrar.
                        </Typography>
                      </Box>
                    </td>
                  </TableRow>
                ) : (
                  sortedRows.map((row) => {
                    const entryDock = getEntryDock(row);
                    const plates = getPersonPlates(row);
                    // El endpoint sólo admite el evento de ingreso de un transporte. Los
                    // acompañantes heredan el dock, pero no tienen una autorización propia para editar.
                    const canEditDock = Boolean(row.eventId && getEntryTransportAuthorization(row));

                    return (
                      <TableRow key={`${row.dni}-${row.fecha}`}>
                        <td>
                          <FechaCell fecha={row.fecha} />
                        </td>
                        <td>
                          <PersonaCell row={row} />
                        </td>
                        <td>
                          {/* La principal en cuerpo normal y las que entraron con ella más chicas,
                              como en la vista por vehículo. */}
                          <div className="gt-patentes">
                            {plates.length === 0 ? (
                              <PatenteChip />
                            ) : (
                              plates.map((unit, index) => (
                                <PatenteChip
                                  key={unit.plate}
                                  patente={unit.plate}
                                  inside={unit.inside}
                                  className={index === 0 ? undefined : "gv-plate-trailer"}
                                />
                              ))
                            )}
                          </div>
                          {row.mainEvent && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              (Acompañante)
                            </Typography>
                          )}
                        </td>
                        <td>
                          <Typography>{row.transportCompanyName || "—"}</Typography>
                        </td>
                        <td>
                          <HostCell row={row} />
                        </td>
                        <td>
                          <span className="gt-dock-cell">
                            <span>{entryDock ?? "-"}</span>
                            {canEditDock && (
                              <Tooltip title="Editar dock asignado">
                                <IconButton
                                  className="dock-edit-button"
                                  aria-label={`Editar dock asignado de ${row.nombre}`}
                                  size="small"
                                  onClick={() => openDockEditor(row)}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </span>
                        </td>
                        <td>
                          <ExitButton onClick={() => setExitRow(row)} />
                        </td>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableWrapper>
        </Box>

        {/* Montado sólo mientras está abierto: el hook de unidades no limpia lo que ya trajo. */}
        {exitRow && (
          <PersonExitDialog
            guardia={exitRow}
            onClose={() => setExitRow(null)}
            onConfirm={handleExitConfirm}
          />
        )}
        <EditAssignedDockDialog
          target={dockTarget}
          onClose={() => setDockTarget(null)}
          onUpdated={onDockUpdated}
        />
      </>
    );
  }
);

GuardiaTable.displayName = "GuardiaTable";

export default GuardiaTable;
